import type { AxiosResponse } from 'axios';
import React, { createContext, useContext } from 'react';
import {
	extractOfDataQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import { useProcessControl } from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose } from '@gov-nx/core/types';
import { useAutorizaceStore } from '@gov-nx/store';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type { FormData, ServiceContextTypes } from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export const VypisVyuzitiUdajuZROBContextProvider = ({
	children,
	code,
}: ServiceContextProviderProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { toastMessageError, toastMessageSuccess } = useMessageEvents();
	const { controls, setControls } = useProcessControl();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const getServiceAuthorizationType = useAutorizaceStore(
		'getServiceAuthorizationType'
	);
	const AUTHORIZATION_SERVICE_NAME = 'UDAJE_ROB_VYUZITI';
	const requiresConsent =
		getServiceAuthorizationType(AUTHORIZATION_SERVICE_NAME) === 'NIZKA';
	const formDefinition = FormInstance({
		code,
		dataBoxTypes: requiredDataBoxes,
		requiresConsent,
	});

	const submitMutation = usePoMutation<AxiosResponse<void>, FormData>({
		mutationFn: compose(extractOfDataQuery(axiosInstance), prepareSubmitData),
		onSuccess: (response) => {
			const nonHighAuthorizationFlow = () => {
				formDefinition.formReset();
				toastMessageSuccess(tsn('form.messages.odeslano'));
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					response.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onError: () => {
			toastMessageError(tsn('form.messages.chyba-pri-odesilani'));
		},
	});

	const onSubmit = formDefinition.formMethods.handleSubmit(() =>
		submitMutation.mutate(formDefinition.formMethods.getValues())
	);

	return (
		<ServiceContext.Provider
			value={{
				code,
				controls,
				formDefinition,
				setControls,
				submitMutation,
				onSubmit,
				requiredDataBoxes,
				requiresConsent,
			}}>
			{children}
		</ServiceContext.Provider>
	);
};

export const useVypisVyuzitiUdajuZROBContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
